import { api } from '~/trpc-extra/api';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { Skeleton, Tooltip, Avatar } from '@mantine/core';
import { IconStrikethrough } from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { isMultitabTestAtom } from '~/atoms/no-code-atoms';
import NewOperationControl from '../../NewOperationControl';
import { DEFAULT_EMPTY_PREV_OPERATION_ORDER } from '../../OperationList';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import Operation from '../../Operation';

export function ComponentCallOpInner({
  componentId,
  testContentId,
  step,
  readonly,
  parentRootOperationId,
  parentOperationOrder,
  parentContexts: incomingParentContexts,
}: {
  componentId: string;
  testContentId: string;
  step: SelectedStep;
  readonly?: boolean;
  parentRootOperationId: string;
  parentOperationOrder: number;
  parentContexts?: OperationParentContext[];
}) {
  const { data: component, isLoading } = api.component.get.useQuery({ id: componentId }, { enabled: !!componentId });
  const hasMultipleTabs = useAtomValue(isMultitabTestAtom);

  const parentContexts = [
    ...(incomingParentContexts ?? []),
    {
      rootOperationId: incomingParentContexts?.at(0)?.rootOperationId ?? parentRootOperationId,
      order: parentOperationOrder,
      componentId,
      parameters: component?.parameters ?? [],
      conditional: undefined,
    },
  ] satisfies OperationParentContext[];

  return (
    isLoading ?
      <div className="flex flex-col gap-1">
        <Skeleton
          height={20}
          width="100%"
        />
        <Skeleton
          height={20}
          width="20%"
        />
        <Skeleton
          height={20}
          width="40%"
        />
      </div>
    : component?.operations?.length ?
      <div className="py-2">
        {component.operations.map((operation, index) => (
          <div key={operation.id}>
            <NewOperationControl
              testContentId={testContentId}
              prevOperationId={index > 0 ? component.operations[index - 1].id : undefined}
              nextOperationId={operation.id}
              stepId={step.id}
              stepOrder={step.stepOrder}
              stepMode={step.mode}
              insertAfterOperationOrder={
                index > 0 ? component.operations[index - 1].order : DEFAULT_EMPTY_PREV_OPERATION_ORDER
              }
              operationId={index > 0 ? component.operations[index - 1].id : undefined}
              activePageAlias={
                index > 0 ?
                  component.operations[index - 1].opensPageAlias || component.operations[index - 1].pageAlias
                : component.operations[0].pageAlias
              }
              parentContexts={parentContexts}
              readonly={readonly}
            />
            <div
              className={clsx('flex items-center gap-2', {
                'opacity-50': operation.skip,
              })}
            >
              {operation.skip ?
                <Tooltip label="Operation will be skipped">
                  <Avatar
                    size="xs"
                    radius="xl"
                  >
                    <IconStrikethrough size={12} />
                  </Avatar>
                </Tooltip>
              : null}
              <Operation
                step={step}
                operation={operation}
                testContentId={testContentId}
                stepIdx={step.stepOrder}
                readonly={readonly}
                parentContexts={parentContexts}
                showTabLabel={
                  !!hasMultipleTabs &&
                  operation.type !== 'DEFINE_VARIABLE' &&
                  operation.type !== 'RECEIVE_EMAIL' &&
                  operation.type !== 'WAIT'
                }
              />
            </div>
          </div>
        ))}
        <NewOperationControl
          testContentId={testContentId}
          prevOperationId={component.operations.at(-1)?.id}
          stepId={step.id}
          stepOrder={step.stepOrder}
          stepMode={step.mode}
          insertAfterOperationOrder={component.operations.at(-1)?.order ?? DEFAULT_EMPTY_PREV_OPERATION_ORDER}
          operationId={component.operations.at(-1)?.id}
          activePageAlias={component.operations.at(-1)?.opensPageAlias || component.operations.at(-1)?.pageAlias}
          parentContexts={parentContexts}
          readonly={readonly}
        />
      </div>
    : <div>
        <div className="text-sm text-gray-500">No actions defined</div>
        <NewOperationControl
          testContentId={testContentId}
          stepId={step.id}
          stepOrder={step.stepOrder}
          stepMode={step.mode}
          insertAfterOperationOrder={DEFAULT_EMPTY_PREV_OPERATION_ORDER}
          activePageAlias="page"
          parentContexts={parentContexts}
          readonly={readonly}
        />
      </div>
  );
}
